<script lang="ts">
  import { AlertCircle, CheckCircle2, Circle } from 'lucide-svelte';
  import InspectorField from '../InspectorField.svelte';
  import { patchUnit, fetchUnitValidation, getErrorMessage } from '$lib/api';
  import { computeUnitWorkflow } from '$lib/workflow';
  import { projectsQuery } from '$lib/queries/projects';
  import { unitsQuery } from '$lib/queries/units';
  import { zonesQuery } from '$lib/queries/zones';
  import { roomsQuery } from '$lib/queries/rooms';
  import { loadsQuery } from '$lib/queries/loads';
  import { inputDevicesQuery } from '$lib/queries/input_devices';
  import { rcusQuery } from '$lib/queries/rcus';
  import { dinModulesForUnitQuery } from '$lib/queries/din_modules';
  import { rcuChannelsQuery } from '$lib/queries/rcu_channels';
  import { scenesQuery } from '$lib/queries/scenes';
  import { unitSceneLoadsQuery } from '$lib/queries/scene_loads';
  import { productsQuery } from '$lib/queries/products';
  import { invalidateAfterUnitUpdate } from '$lib/state/invalidation';
  import { router } from '$lib/router';
  import { uiState, setLoading, clearError } from '$lib/state/ui.svelte';
  import { showSuccess, showError } from '$lib/state/toast.svelte';

  const projects = projectsQuery();
  const units = unitsQuery();
  const zones = zonesQuery();
  const rooms = roomsQuery();
  const loads = loadsQuery();
  const devices = inputDevicesQuery();
  const rcus = rcusQuery();
  const dinModules = dinModulesForUnitQuery();
  const rcuChannels = rcuChannelsQuery();
  const scenes = scenesQuery();
  const sceneLoads = unitSceneLoadsQuery();
  const products = productsQuery();

  let validating = $state(false);

  let unitId = $derived(router.unitId ?? 0);
  let unit = $derived((units.data ?? []).find((u) => u.id === unitId) ?? null);
  let project = $derived((projects.data ?? []).find((p) => p.id === router.projectId) ?? null);
  let unitZones = $derived(zones.data ?? []);
  let unitRooms = $derived(rooms.data ?? []);
  let unitLoads = $derived(loads.data ?? []);
  let unitDevices = $derived(devices.data ?? []);
  let unitInputKeys = $derived((devices.data ?? []).flatMap((d) => d.keys ?? []));
  let unitChannels = $derived(
    (rcuChannels.data ?? []).map((c) => ({ load: c.load, object_id: c.rcu }))
  );

  let workflow = $derived(
    computeUnitWorkflow({
      unit: unit,
      zones: unitZones,
      rooms: unitRooms,
      loads: unitLoads,
      inputDevices: unitDevices,
      inputKeys: unitInputKeys,
      rcus: rcus.data ?? [],
      dinModules: dinModules.data ?? [],
      channels: unitChannels,
      scenes: scenes.data ?? [],
      sceneLoads: sceneLoads.data ?? [],
      products: products.data ?? []
    })
  );

  let unitRcus = $derived((rcus.data ?? []).filter((r) => r.unit === unitId));
  let unitScenes = $derived((scenes.data ?? []).filter((s) => s.unit === unitId));
  let unitDinModules = $derived(
    (dinModules.data ?? []).filter((m) => unitRcus.some((rcu) => rcu.id === m.rcu))
  );

  async function saveName(name: string) {
    if (!unit) return;
    await patchUnit(unit.id, { name });
    invalidateAfterUnitUpdate(unit.project);
  }

  async function saveNotes(notes: string) {
    if (!unit) return;
    await patchUnit(unit.id, { notes });
    invalidateAfterUnitUpdate(unit.project);
  }

  async function runValidation() {
    if (!unit || validating) return;
    validating = true;
    setLoading(true);
    clearError();
    try {
      const result = (await fetchUnitValidation(unit.id)) as {
        issues?: unknown[];
        errors?: unknown[];
        issue_count?: number;
      } | null;
      const issues = result?.issues || result?.errors || [];
      uiState.validationResults = issues;
      if ((result?.issues?.length ?? 0) > 0 || (result?.issue_count ?? 0) > 0) {
        const count = result?.issue_count ?? result?.issues?.length ?? 0;
        showError(`${count} issue${count === 1 ? '' : 's'} found.`);
      } else {
        showSuccess('Unit validation passed — ready for export.');
      }
    } catch (e) {
      showError(getErrorMessage(e) || 'Validation failed');
    } finally {
      validating = false;
      setLoading(false);
    }
  }

  function stageChipClass(status: string) {
    if (status === 'complete') return 'border-state-ok/30 bg-state-ok/5 text-state-ok';
    if (status === 'attention') return 'border-state-warn/30 bg-state-warn/5 text-state-warn';
    return 'border-border-default bg-bg-canvas text-text-muted';
  }
</script>

{#if unit}
  <div class="px-3 py-3 space-y-4">
    <div>
      <h2 class="text-sm font-medium text-text-primary mb-3">{unit.name}</h2>
      <div class="space-y-2">
        <InspectorField label="Name" value={unit.name} onSave={saveName} />
        <InspectorField
          label="Notes"
          type="textarea"
          value={unit.notes || ''}
          placeholder="Shipment-specific details, variations, handover notes…"
          onSave={saveNotes}
        />
      </div>
    </div>

    <div class="space-y-2">
      <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Stats</p>
      <dl class="grid grid-cols-2 gap-x-2 gap-y-1 text-sm">
        <dt class="text-text-secondary">Project</dt>
        <dd class="text-text-primary truncate">{project?.name ?? '—'}</dd>
        <dt class="text-text-secondary">Zones</dt>
        <dd class="text-text-primary font-mono">{unitZones.length}</dd>
        <dt class="text-text-secondary">Rooms</dt>
        <dd class="text-text-primary font-mono">{unitRooms.length}</dd>
        <dt class="text-text-secondary">Loads</dt>
        <dd class="text-text-primary font-mono">{unitLoads.length}</dd>
        <dt class="text-text-secondary">RCUs</dt>
        <dd class="text-text-primary font-mono">{unitRcus.length}</dd>
        <dt class="text-text-secondary">DIN modules</dt>
        <dd class="text-text-primary font-mono">{unitDinModules.length}</dd>
        <dt class="text-text-secondary">Devices</dt>
        <dd class="text-text-primary font-mono">{unitDevices.length}</dd>
        <dt class="text-text-secondary">Scenes</dt>
        <dd class="text-text-primary font-mono">{unitScenes.length}</dd>
      </dl>
    </div>

    <div class="space-y-2">
      <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Workflow</p>
      <div class="space-y-1.5">
        {#each workflow.stages as stage}
          <div
            class="flex items-center gap-2 px-2 py-1.5 rounded border text-xs {stageChipClass(
              stage.status
            )}"
          >
            <span class="shrink-0">
              {#if stage.status === 'complete'}
                <CheckCircle2 class="w-3.5 h-3.5" />
              {:else if stage.status === 'attention'}
                <AlertCircle class="w-3.5 h-3.5" />
              {:else}
                <Circle class="w-3.5 h-3.5" />
              {/if}
            </span>
            <span class="flex-1 min-w-0">
              <span class="font-medium">{stage.label}</span>
              <span class="block text-2xs opacity-80 truncate">{stage.detail}</span>
            </span>
          </div>
        {/each}
      </div>
    </div>

    <button
      type="button"
      class="btn-primary w-full text-sm"
      disabled={validating}
      onclick={runValidation}
    >
      {validating ? 'Validating…' : 'Run validation'}
    </button>
  </div>
{:else}
  <div class="px-3 py-3 text-sm text-text-muted">No unit selected</div>
{/if}
